import { User, UserProfile } from './auth';
import { ProductModel } from './product';

type ScheduleType = 'oneday' | 'regular' | 'chat';
export type ChildInfoKeyType = 'name' | 'birthday' | 'gender';
export class ChildInfo {
  name?: string;
  birthday?: string;
  gender?: string;
  mbtiType?: string;
  mbtiLastTestedBy?: string;

  constructor(data: {
    name?: string;
    birthday?: string;
    gender?: string;
    mbtiType?: string;
    mbtiLastTestedBy?: string;
  }) {
    this.name = data.name;
    this.birthday = data.birthday;
    this.gender = data.gender;
    this.mbtiType = data.mbtiType;
    this.mbtiLastTestedBy = data.mbtiLastTestedBy;
  }

  toString() {
    return `${this.name}-${this.birthday}-${this.gender}`;
  }
}
export interface ClassInquiryInfo {
  address?: any;
  classTitle?: string;
  children?: ChildInfo[];
  days?: string[];
  scheduleType?: ScheduleType;
  scheduleWithChat?: boolean;
}

export enum MessageType {
  WELCOME_WARNING = 'welcome_warning',
  IMAGE = 'image',
  CLASS_INQUIRY = 'class_inquiry',
  CHAT_END = 'chat_end',
  PAYMENT_REQUEST = 'payment_request',
  PAYMENT_COMPLETE = 'payment_complete',
  CLASS_REPORT = 'class_report',
  CLASS_PERIOD_EXTEND = 'class_period_extend',
  TEXT = 'text',
}

export interface LegacyChatMessage {
  _id: string;
  type?: MessageType;
  status?: string;
  text: string;
  tutorId: string;
  parentId: string;
  classId: string;
  createdAt: string;
  chatId: string;
  chatRoomId: string;
  classCardId: string;
  makecard: string;
  payment: string;
  writereport: string;
  prolong: string;
  leave: string;
  address: string;
  parentChild: string;
  image: boolean;
  images: ChatImage[];
  targetAppToken?: string;
  readAt: string;
  updatedAt?: string;
}

export interface ExtendedLegacyChatMessage extends LegacyChatMessage {
  class: ProductModel;
  tutor: User;
  parent: User;
}

export interface ChatImage {
  height: number;
  width: number;
  imageUrl: string;
}

export interface LegacyChatRoom {
  _id: string;
  classId: string;
  parentId: string;
  tutorId: string;
  createdAt: string;
  updatedAt: string;
  chattext: string;
  unreadParentCount: number;
  unreadTutorCount: number;
  commentTutorCount: number;
  tutorLastMessageAt: string;
  unreadParentPushCount: number;
  parentLastMessageAt: string;
  unreadTutorPushCount: number;
  tutorPin: boolean;
  pin: boolean;
  inquiryInfo?: ClassInquiryInfo;
}

export interface ExtendedLegacyChatRoom extends LegacyChatRoom {
  class: ProductModel;
  tutor: User;
  parent: User;
}

export type RemindChatType = 'tutor' | 'parent';

// V2 ChatRoom

export enum ChatRoomType {
  CS = 'cs',
}

export enum ConsultationStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

export enum ChatRole {
  MASTER = 'master',
  MANAGER = 'manager',
  MEMBER = 'member',
}

export interface Participant {
  id: string;
  role: ChatRole;
  unreadCount?: number;
  isPinned?: boolean;
  isHidden?: boolean;
  lastReadAt?: Date;
}

interface ChatRoomContext {
  productIds?: string[];
  history?: string[];
}

export interface ChatRoom {
  _id: string;
  type: ChatRoomType;
  participants: Participant[];
  nickname?: string;
  lastMessage?: string;
  consultationStatus?: ConsultationStatus;
  lastMessageAt?: Date;
  context?: ChatRoomContext;
  createdAt: Date;
  updatedAt: Date;
}

export interface GetChatRoom extends ChatRoom {
  participants: (Participant & { profile: UserProfile })[];
}

// V2 ChatMessage

export enum CardMessageBtnType {
  PROGRAM_REPORT = 'program_report',
  PROGRAM_INVOICE = 'program_invoice',
  PAYMENT_STATEMENT = 'payment_statement',
  REVIEW = 'review',
  PRODUCT_DETAIL = 'product_detail',
  PARTNER_PROFILE = 'partner_profile',
  LINK = 'link',
}
export enum ChatMessageType {
  TEXT = 'text',
  IMAGE = 'image',
  FILE = 'file',
  CARD = 'card',
  BOX = 'box',
}
export interface FileMessageContent {
  originName: string;
  fileUrl: string;
}
export interface CardMessageContent {
  title: string;
  content: string;
  headerIcon?: string;
  imageUrl?: string;
  tagInfo?: {
    uid: string;
    type: string; // "product"
    text: string;
  };
  btnInfos?: CardMessageBtnInfo[];
}
export interface BoxMessageContent {
  title: string;
  messages: string[];
}

interface ProgramReportButtonState {
  programReportId: string;
}

interface ReviewButtonState {
  productId: string;
  paymentStatementId: string;
}

interface PartnerProfileButtonState {
  partnerId: string;
}

interface ProgramInvoiceButtonState {
  programInvoiceId: string;
}

interface PaymentStatementButtonState {
  paymentStatementId: string;
}

interface BaseButtonState {
  productId: string;
  classInvoiceId?: string;
  paymentStatementId?: string;
}

interface ProgramReportButtonInfo {
  type: CardMessageBtnType.PROGRAM_REPORT;
  label: string;
  state: ProgramReportButtonState;
}

interface ReviewButtonInfo {
  type: CardMessageBtnType.REVIEW;
  label: string;
  state: ReviewButtonState;
}

interface PartnerProfileButtonInfo {
  type: CardMessageBtnType.PARTNER_PROFILE;
  label: string;
  state: PartnerProfileButtonState;
}

interface ProgramInvoiceButtonInfo {
  type: CardMessageBtnType.PROGRAM_INVOICE;
  label: string;
  state: ProgramInvoiceButtonState;
}

interface PaymentStatementButtonInfo {
  type: CardMessageBtnType.PAYMENT_STATEMENT;
  label: string;
  state: PaymentStatementButtonState;
}

interface OtherButtonInfo {
  type: Exclude<
    CardMessageBtnType,
    | CardMessageBtnType.PROGRAM_REPORT
    | CardMessageBtnType.REVIEW
    | CardMessageBtnType.PARTNER_PROFILE
    | CardMessageBtnType.PROGRAM_INVOICE
    | CardMessageBtnType.PAYMENT_STATEMENT
  >;
  label: string;
  linkTo?: string;
  state: BaseButtonState;
}

export type CardMessageBtnInfo =
  | ProgramReportButtonInfo
  | ReviewButtonInfo
  | PartnerProfileButtonInfo
  | ProgramInvoiceButtonInfo
  | PaymentStatementButtonInfo
  | OtherButtonInfo;

interface TextMessageData {
  type: ChatMessageType.TEXT;
  content: string;
}
interface CardMessageData {
  type: ChatMessageType.CARD;
  content: CardMessageContent;
}
interface ImageMessageData {
  type: ChatMessageType.IMAGE;
  content: string[];
}
interface FileMessageData {
  type: ChatMessageType.FILE;
  content: FileMessageContent;
}
interface BoxMessageData {
  type: ChatMessageType.BOX;
  content: BoxMessageContent;
}

type ChatMessageData = TextMessageData | CardMessageData | ImageMessageData | FileMessageData | BoxMessageData;

export interface ChatMessage {
  _id: string;
  uuid: string;
  senderId: string;
  chatRoomId: string;
  data: ChatMessageData;
  createdAt: Date;
  updatedAt: Date;
}
